import { PayloadAction } from '@reduxjs/toolkit';
import { put, select } from 'redux-saga/effects';

import * as API from 'services';

import { MyPageSelectors } from '../my-page';
import { IPayloadGetWorkerApplicantDetailRequest } from '../worker-applicant';
import {
	formatUploadExcelFranchiseFailedList,
	formatWorkerFranchiseAssignmentList,
	formatWorkerFranchiseDetail,
	formatWorkerFranchiseList,
	formatWorkerFranchiseLodgingList,
	formatWorkerFranchiseProgramList,
	formatWorkerFranchiseRecommenderInfoList,
	formatWorkerFranchiseRequest,
} from './worker-franchise.helper';
import { WorkerFranchiseActions } from './worker-franchise.redux';
import {
	IPayloadApproveWorkerFranchiseProgramRequest,
	IPayloadCreateWorkerFranchiseLodgingRequest,
	IPayloadCreateWorkerFranchiseProgramRequest,
	IPayloadCreateWorkerFranchiseRequest,
	IPayloadDeleteWorkerFranchiseAssignmentRequest,
	IPayloadDeleteWorkerFranchiseLodgingRequest,
	IPayloadDeleteWorkerFranchiseProgramRequest,
	IPayloadDeleteWorkerFranchiseRecommenderRequest,
	IPayloadDeleteWorkerFranchiseRequest,
	IPayloadGetWorkerFranchiseAssignmentListRequest,
	IPayloadGetWorkerFranchiseDetailRequest,
	IPayloadGetWorkerFranchiseExcelListRequest,
	IPayloadGetWorkerFranchiseListRequest,
	IPayloadGetWorkerFranchiseLodgingListRequest,
	IPayloadGetWorkerFranchiseProgramListRequest,
	IPayloadRejectWorkerFranchiseProgramRequest,
	TPayloadUploadExcelWorkerFranchiseRequest,
} from './worker-franchise.type';

export function* getWorkerFranchiseListSaga(
	action: PayloadAction<IPayloadGetWorkerFranchiseListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerFranchiseListResponse> =
			yield API.getWorkerFranchiseList({
				searchTypes: formatWorkerFranchiseRequest(searchTypes),
				...rest,
			});
		const content = formatWorkerFranchiseList({ input: res.data.content });

		yield put(
			WorkerFranchiseActions.getWorkerFranchiseListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.getWorkerFranchiseListFailure());
	}
}

export function* getWorkerFranchiseExcelListSaga(
	action: PayloadAction<IPayloadGetWorkerFranchiseExcelListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerFranchiseListResponse> =
			yield API.getWorkerFranchiseList({
				searchTypes: formatWorkerFranchiseRequest(searchTypes),
				...rest,
			});
		const content = formatWorkerFranchiseList({
			input: res.data.content,
			isDownloadExcel: true,
		});

		yield put(WorkerFranchiseActions.getWorkerFranchiseExcelListSuccess());
		callback?.({ data: content });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerFranchiseActions.getWorkerFranchiseExcelListFailure());
	}
}

export function* createWorkerFranchise(
	action: PayloadAction<IPayloadCreateWorkerFranchiseRequest>
) {
	const { callback, ...props } = action.payload;

	try {
		yield API.createWorkerFranchise({
			...props,
		});

		yield put(WorkerFranchiseActions.createWorkerFranchiseSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.createWorkerFranchiseFailure());
	}
}

export function* getWorkerFranchiseDetail(
	action: PayloadAction<IPayloadGetWorkerFranchiseDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerFranchiseDetailResponse> =
			yield API.getWorkerFranchiseDetail(id);

		const workerFranchiseDetail = formatWorkerFranchiseDetail(res.data);

		yield put(
			WorkerFranchiseActions.getWorkerFranchiseDetailSuccess({
				workerFranchiseDetail,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.getWorkerFranchiseDetailFailure());
	}
}

export function* deleteWorkerFranchise(
	action: PayloadAction<IPayloadDeleteWorkerFranchiseRequest>
) {
	const { callback, ids } = action.payload;

	try {
		yield API.deleteWorkerFranchise(ids);

		yield put(WorkerFranchiseActions.deleteWorkerFranchiseSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.deleteWorkerFranchiseFailure());
	}
}

// TODO: franchise assignment
export function* getWorkerFranchiseAssignmentList(
	action: PayloadAction<IPayloadGetWorkerFranchiseAssignmentListRequest>
) {
	const { callback, id, params } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerFranchiseAssignmentListResponse> =
			yield API.getWorkerFranchiseAssignmentList(id, params);
		const content = formatWorkerFranchiseAssignmentList(res?.data?.content);

		yield put(
			WorkerFranchiseActions.getWorkerFranchiseAssignmentListSuccess({
				...params,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.getWorkerFranchiseAssignmentListFailure());
	}
}
export function* deleteWorkerFranchiseAssignmentList(
	action: PayloadAction<IPayloadDeleteWorkerFranchiseAssignmentRequest>
) {
	const { callback, franchiseId, ids } = action.payload;

	try {
		// call api
		yield API.deleteWorkerFranchiseAssignmentList(franchiseId, ids);

		yield put(WorkerFranchiseActions.deleteWorkerFranchiseAssignmentSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.deleteWorkerFranchiseAssignmentFailure());
	}
}

// TODO: franchise lodging
export function* getWorkerFranchiseLodgingList(
	action: PayloadAction<IPayloadGetWorkerFranchiseLodgingListRequest>
) {
	const { callback, id, params } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerFranchiseLodgingListResponse> =
			yield API.getWorkerFranchiseLodgingList(id, params);
		const content = formatWorkerFranchiseLodgingList(res?.data?.content);

		yield put(
			WorkerFranchiseActions.getWorkerFranchiseLodgingListSuccess({
				...params,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.getWorkerFranchiseLodgingListFailure());
	}
}
export function* deleteWorkerFranchiseLodgingList(
	action: PayloadAction<IPayloadDeleteWorkerFranchiseLodgingRequest>
) {
	const { callback, franchiseId, ids } = action.payload;

	try {
		// call api
		yield API.deleteWorkerFranchiseLodgingList(franchiseId, ids);

		yield put(WorkerFranchiseActions.deleteWorkerFranchiseLodgingSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.deleteWorkerFranchiseLodgingFailure());
	}
}
export function* createWorkerFranchiseLodging(
	action: PayloadAction<IPayloadCreateWorkerFranchiseLodgingRequest>
) {
	const { callback, franchiseId, ...data } = action.payload;

	try {
		yield API.createWorkerFranchiseLodging(franchiseId, data);

		yield put(WorkerFranchiseActions.createWorkerFranchiseLodgingSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.createWorkerFranchiseLodgingFailure());
	}
}

// TODO: franchise program
export function* getWorkerFranchiseProgramList(
	action: PayloadAction<IPayloadGetWorkerFranchiseProgramListRequest>
) {
	const { callback, id, params } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerFranchiseProgramListResponse> =
			yield API.getWorkerFranchiseProgramList(id, params);
		const content = formatWorkerFranchiseProgramList(res?.data?.content);

		yield put(
			WorkerFranchiseActions.getWorkerFranchiseProgramListSuccess({
				...params,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.getWorkerFranchiseProgramListFailure());
	}
}
export function* deleteWorkerFranchiseProgramList(
	action: PayloadAction<IPayloadDeleteWorkerFranchiseProgramRequest>
) {
	const { callback, franchiseId, ids } = action.payload;

	try {
		// call api
		yield API.deleteWorkerFranchiseProgramList(franchiseId, ids);

		yield put(WorkerFranchiseActions.deleteWorkerFranchiseProgramSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.deleteWorkerFranchiseProgramFailure());
	}
}
export function* createWorkerFranchiseProgram(
	action: PayloadAction<IPayloadCreateWorkerFranchiseProgramRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.createWorkerFranchiseProgram(data);

		yield put(WorkerFranchiseActions.createWorkerFranchiseProgramSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.createWorkerFranchiseProgramFailure());
	}
}

export function* uploadExcelWorkerFranchise(
	action: PayloadAction<TPayloadUploadExcelWorkerFranchiseRequest>
) {
	const { data, callback, customFields } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TUploadExcelFranchiseResponse> =
			yield API.uploadExcelWorkerFranchise(data);

		const dataMyPage: ReturnType<typeof MyPageSelectors.selectDataMyPage> =
			yield select(MyPageSelectors.selectDataMyPage);
		const userInfo: API.UploadExcelUserInfo = {
			districtId: dataMyPage?.districtId,
			districtName: dataMyPage?.districtName,
			role: dataMyPage?.role,
		};

		const failedList = formatUploadExcelFranchiseFailedList({
			input: res?.data,
			customFields,
			userInfo,
		});

		const successCount = data?.length - (failedList?.length || 0);
		const failCount = failedList?.length || 0;

		yield put(
			WorkerFranchiseActions.uploadExcelWorkerFranchiseSuccess({
				failedList,
				successCount,
				failCount,
			})
		);
		callback?.({
			failCount,
			successCount,
		});
	} catch (error: any) {
		callback?.({
			error,
			failCount: 0,
			successCount: 0,
		});
		yield put(WorkerFranchiseActions.uploadExcelWorkerFranchiseFailure());
	}
}

export function* getWorkerFranchiseRecommenderSaga(
	action: PayloadAction<IPayloadGetWorkerApplicantDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerFranchiseRecommenderInfoListResponse> =
			yield API.getWorkerFranchiseRecommenderInfo(id);

		const content = formatWorkerFranchiseRecommenderInfoList(res.data);

		yield put(
			WorkerFranchiseActions.getWorkerFranchiseRecommenderInfoSuccess({
				workerFranchiseRecommenderInfo: content,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerFranchiseActions.getWorkerFranchiseRecommenderInfoFailure()
		);
	}
}

export function* deleteWorkerFranchiseRecommenderSaga(
	action: PayloadAction<IPayloadDeleteWorkerFranchiseRecommenderRequest>
) {
	const { callback, ids, recommenderId } = action.payload;

	try {
		yield API.deleteWorkerFranchiseRecommender(recommenderId, ids);

		yield put(WorkerFranchiseActions.deleteWorkerFranchiseRecommenderSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.deleteWorkerFranchiseRecommenderFailure());
	}
}

export function* approveWorkerFranchiseProgram(
	action: PayloadAction<IPayloadApproveWorkerFranchiseProgramRequest>
) {
	const { callback, franchiseId, programIds } = action.payload;

	try {
		yield API.approveWorkerFranchiseProgram(franchiseId, programIds);
		callback?.();
		yield put(WorkerFranchiseActions.approveWorkerFranchiseProgramSuccess());
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.approveWorkerFranchiseProgramFailure());
	}
}

export function* rejectWorkerFranchiseProgram(
	action: PayloadAction<IPayloadRejectWorkerFranchiseProgramRequest>
) {
	const { callback, franchiseId, programIds } = action.payload;

	try {
		yield API.rejectWorkerFranchiseProgram(franchiseId, programIds);
		callback?.();
		yield put(WorkerFranchiseActions.rejectWorkerFranchiseProgramSuccess());
	} catch (error) {
		callback?.(error);
		yield put(WorkerFranchiseActions.rejectWorkerFranchiseProgramFailure());
	}
}
